import _ from 'lodash';
import { watch, onBeforeUnmount } from '@vue/composition-api';

import { useNewEvents } from './use-new-events-store';
import { useSessionsGetters } from '@/modules/sessions-store';
import { HITL } from '@/api';

/** Track visible + ongoing sessions and subscribe to their events */
export const useSubscribeToNewEvents = () => {
  const {
    visibleOpenSessionIds,
    visibleOpenForAgentSessionIds,
  } = useSessionsGetters();
  const {
    subscriptions,
    initializeNewContentEventsCounters,
    clearCountersBySessionIds,
  } = useNewEvents();

  let stopTracking: () => void;

  const setWSConnectionId = (callbackId: string) => {
    stopTracking ? stopTracking() : null;

    /* We can start watching when WS connection is established */
    const sources = [visibleOpenSessionIds, visibleOpenForAgentSessionIds];
    subscribeToEventsThrottled.cancel();

    stopTracking = watch(sources, (current, previous) => {
      subscribeToEventsThrottled({ callbackId, current, previous });
    }, { immediate: true });
  };

  const subscribeToEvents = async (args: {
    callbackId: string;
    current?: string[][];
    previous?: (string[] | undefined)[];
  }) => {
    const { callbackId, current = [], previous = [] } = args;

    /* We listen to new events in sessions claimed by other agents as well */
    const cOpen = current[0] || [];
    const pOpen = previous[0] || [];
    if (!_.isEqual(cOpen, pOpen)) {
      /* Subscribe first, so we wont miss any events in between requests */
      await HITL.listeners.updateListener({
        callbackId,
        sessionIds: cOpen,
      });
    }

    /* If new sessions become visible + available request their unread events */
    const cAvailable = current[1] || [];
    const pAvailable = previous[1] || [];

    const added = _.difference(cAvailable, pAvailable);
    if (added.length) {
      await initializeNewContentEventsCounters({ sessionIds: added });
    }

    /* When session becomes hidden or unavailable, clear unread events data */
    const deleted = _.difference(pAvailable, cAvailable);
    if (deleted.length) {
      clearCountersBySessionIds({ sessionIds: deleted });
    }

    subscriptions.value = cOpen;
  };

  /* Limit number of calls due to conversation list pagination behavior */
  const subscribeToEventsThrottled = _.throttle(subscribeToEvents, 500, {
    trailing: true,
  });

  /* Auto clean-up watcher */
  onBeforeUnmount(() => {
    stopTracking ? stopTracking() : null;
  });

  return { setWSConnectionId };
};
